import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface NavbarProps {
  categories: { name: string; url: string; icon: string; image: string }[];
}

export default function Navbar({ categories }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const showTransparentNav = isHomePage && !scrolled;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        showTransparentNav
          ? 'bg-black/15 backdrop-blur-md'
          : 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 flex-shrink-0">
            <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-table-line text-background-50 text-sm"></i>
            </div>
            <span className={`font-heading font-semibold text-lg transition-colors duration-300 ${
              showTransparentNav ? 'text-background-50' : 'text-foreground-950'
            }`}>
              BasetaoSheet
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-4">
            <Link
              to="/"
              className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                showTransparentNav
                  ? 'text-background-50/90 hover:text-background-50 hover:bg-background-50/10'
                  : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
              }`}
            >
              <i className="ri-home-4-line text-sm"></i>
              <span>Home</span>
            </Link>

            <Link
              to="/basetao-spreadsheet-guide"
              className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                showTransparentNav
                  ? 'text-background-50/90 hover:text-background-50 hover:bg-background-50/10'
                  : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
              }`}
            >
              <i className="ri-book-read-line text-sm"></i>
              <span>Shopping Guide</span>
            </Link>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  showTransparentNav
                    ? 'text-background-50/90 hover:text-background-50 hover:bg-background-50/10'
                    : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                }`}
              >
                <span>Product Categories</span>
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 left-0 w-64 bg-background-50 rounded-xl border border-background-200/70 shadow-lg py-2 max-h-80 overflow-y-auto animate-[fadeIn_0.2s_ease-out]">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setDropdownOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer"
                    >
                      <div className="w-7 h-7 rounded-md bg-background-100 flex items-center justify-center flex-shrink-0">
                        <i className={`${cat.icon} text-foreground-500 text-xs`}></i>
                      </div>
                      <span className="whitespace-nowrap">{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 rounded-lg text-sm font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              basetao spreadsheet
            </a>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 rounded-lg text-sm font-medium bg-accent-500 text-background-50 hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Buy It Now
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
              showTransparentNav
                ? 'text-background-50 hover:bg-background-50/10'
                : 'text-foreground-950 hover:bg-background-100'
            }`}
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {mobileOpen && (
          <div className="md:hidden bg-background-50 rounded-xl border border-background-200/70 shadow-lg p-4 mb-4 animate-[fadeIn_0.2s_ease-out] max-h-[70vh] overflow-y-auto">
            <div className="flex items-center gap-3 mb-4 pb-3 border-b border-background-200/70">
              <Link
                to="/"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors"
              >
                <i className="ri-home-4-line"></i>
                Home
              </Link>
              <Link
                to="/basetao-spreadsheet-guide"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors"
              >
                <i className="ri-book-read-line"></i>
                Shopping Guide
              </Link>
            </div>

            <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3 px-2">Browse Categories</p>
            <div className="grid grid-cols-2 gap-2">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="flex flex-col items-center gap-1.5 p-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                    <img
                      src={cat.image}
                      alt={cat.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <span className="text-xs font-medium text-foreground-700 text-center leading-tight">{cat.name}</span>
                </a>
              ))}
            </div>

            <div className="flex flex-col gap-2 pt-4 mt-3 border-t border-background-200/70">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="w-full px-4 py-2.5 rounded-lg text-sm font-semibold bg-primary-500 text-background-50 text-center hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                basetao spreadsheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="w-full px-4 py-2.5 rounded-lg text-sm font-semibold bg-accent-500 text-background-50 text-center hover:bg-accent-600 transition-colors whitespace-nowrap"
              >
                Buy It Now
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}